import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { ZIndex } from "@/domains/design/constants";
import { useCallback, useRef } from "react";
import styled from "@emotion/styled";
import {
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextFieldSlotName,
  MDSTextVariant,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSToggle,
  MDSIconButton,
  MDSIconButtonVariant,
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
} from "@/mds";
import { useHotkey } from "@/domains/hotkeys/use-hotkey";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";

export const QuickSearchModalSearchBar = observer(function QuickSearchModalSearchBar() {
  const { pageStore } = useAppStore();
  const state = pageStore.quickSearchModal;
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * If someone clicks on the search bar, we prevent the event from bubbling
   * out further (to the modal, etc.)
   */
  const onClickHandler = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleInputRef = useCallback(
    (elem: HTMLTextAreaElement | null) => {
      inputRef.current = elem;
      state.handleRef(elem);
    },
    [state]
  );

  const hotkeyOptions = { enabled: state.isOpen, target: inputRef };

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_previous_result),
    () => {
      state.highlightPrevious();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_next_result),
    () => {
      state.highlightNext();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.quick_search_select_highlighted_result),
    event => {
      state.selectHighlighted(event);
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape_to_close),
    () => {
      state.close();
    },
    hotkeyOptions
  );

  return (
    <Container onClick={onClickHandler}>
      <SearchBarShell>
        <StyledTextField
          ref={handleInputRef}
          variant={MDSTextFieldVariant.Brand}
          placeholder="Search or ask your Mem"
          defaultValue={state.value}
          isTextArea={true}
          textAreaProps={{
            rows: 1,
          }}
          onChange={state.handleValueChange}
          onBlur={state.handleBlur}
          onFocus={state.handleFocus}
          onSubmit={state.handleSearch}
        >
          <MDSIcon
            kind={MDSIconKind.Logo}
            boxSize={24}
            size={22}
            slot={MDSTextFieldSlotName.LeftAddon}
            color={
              state.isFocused || state.value.length > 0
                ? MDSIconColor.Brand
                : MDSIconColor.Placeholder
            }
          />
          <RightControls slot={MDSTextFieldSlotName.RightAddon}>
            {state.isAgenticSearchAvailable && (
              <AgenticToggleLabel>
                <MDSButton variant={MDSButtonVariant.Default} size={MDSButtonSize.Small}>
                  <MDSToggle
                    checked={state.isAgenticSearchEnabled}
                    onChange={event => state.setAgenticSearchEnabled(event.target.checked)}
                  />
                  <AgenticToggleEmoji aria-label="Agentic search">🤖</AgenticToggleEmoji>
                </MDSButton>
              </AgenticToggleLabel>
            )}
            <MDSIconButton
              iconKind={MDSIconKind.ArrowBrand}
              onClick={state.handleSearch}
              isDisabled={!state.value.trim()}
              variant={MDSIconButtonVariant.Brand}
              rounded
              data-test-id="text-field-submit-button"
            />
          </RightControls>
        </StyledTextField>
      </SearchBarShell>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 70px;
  padding-bottom: ${({ theme }) => theme.spacing["7"]};
  background-color: ${({ theme }) => theme.colors.surface.chrome};
`;

const SearchBarShell = styled.div`
  position: relative;
  width: calc(100vw - 32px);
  max-width: 920px;
`;

const StyledTextField = styled(MDSTextField)`
  height: auto;
  margin: 0 auto;
  min-height: 68px;
  padding: 0 28px;
  border-radius: 40px;
  width: 100%;
  z-index: ${ZIndex.QuickSearchModel};
  gap: ${({ theme }) => theme.spacing["0.5"]};
  background: ${({ theme }) => theme.colors.background.input.onAccent.focused};

  > textarea {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextM]};
    line-height: 130%;
    color: ${({ theme }) => theme.colors.text.contrast};
  }

  > div:last-of-type {
    width: auto;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["4"]};
`;

const AgenticToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const AgenticToggleEmoji = styled.span`
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1;
`;
